import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Floo Network Phase 6 — Scope Switcher.
 *
 * Client-side "active view scope" model. Distinct from
 * `membership.scope_type` (the permission boundary), this is which scope
 * the user is currently *looking* at within their allowed boundary.
 *
 * Hard rules:
 *   • The active view scope NEVER exceeds the user's membership boundary.
 *   • The hook does NOT mutate `memberships` and does NOT call any
 *     Phase 5 assignment actions. View-state only.
 *   • Hierarchy data is fetched via RLS-gated direct SELECTs — same
 *     query keys as the Floo Network Map so a single react-query cache
 *     entry serves both consumers.
 *   • Persisted in localStorage keyed by user id; revalidated on every
 *     load, with a safe fallback to the boundary's natural scope when
 *     the stored value is no longer reachable (e.g., a region was
 *     archived after the user picked it).
 *   • Archived regions / districts and closed shops are filtered out of
 *     the options. Phase 6 prefers hide-not-disable per spec.
 */

export type FlooScopeType = 'organization' | 'region' | 'district' | 'shop';

export interface FlooScopeOption {
  type: FlooScopeType;
  id: string;
  label: string;
  parentLabel?: string;
  levelLabel: string;
}

export interface UseFlooScopeOptionsResult {
  options: FlooScopeOption[];
  activeScope: FlooScopeOption | null;
  setActiveScope: (option: FlooScopeOption | null) => void;
  isLoading: boolean;
  error: Error | null;
  canSwitchScope: boolean;
  /** The user's effective permission boundary (the upper bound on activeScope). */
  boundary: { type: FlooScopeType; id: string | null } | null;
}

const LEVEL_LABEL: Record<FlooScopeType, string> = {
  organization: 'Organization',
  region: 'Region',
  district: 'District',
  shop: 'Shop',
};

// Rank used to compare scope levels — higher = broader. The active scope
// is always at or below the boundary's level.
const LEVEL_RANK: Record<FlooScopeType, number> = {
  shop: 0,
  district: 1,
  region: 2,
  organization: 3,
};

function storageKey(userId: string | null): string | null {
  if (!userId) return null;
  return `floo-active-scope:${userId}`;
}

function readStored(userId: string | null): { type: FlooScopeType; id: string } | null {
  const key = storageKey(userId);
  if (!key || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { type?: string; id?: string };
    if (
      (parsed.type === 'organization' || parsed.type === 'region' ||
       parsed.type === 'district' || parsed.type === 'shop') &&
      typeof parsed.id === 'string' && parsed.id.length > 0
    ) {
      return { type: parsed.type, id: parsed.id };
    }
    return null;
  } catch {
    return null;
  }
}

function writeStored(userId: string | null, value: { type: FlooScopeType; id: string } | null): void {
  const key = storageKey(userId);
  if (!key || typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // localStorage may be disabled (private browsing); fail silently —
    // the in-memory state still works for the current session.
  }
}

export function useFlooScopeOptions(): UseFlooScopeOptionsResult {
  const { user, membership, activeShop, userShops } = useAuth();
  const userId = user?.id ?? null;

  // ── Boundary derivation ─────────────────────────────────────────────
  // OWNER and ORG_ADMIN always have organization-scope reach inside
  // their org regardless of what `memberships.scope_type` says. Other
  // management roles use their membership's literal scope.
  const boundary: { type: FlooScopeType; id: string | null } | null = useMemo(() => {
    if (!membership) return null;
    const role = membership.role as string | undefined;
    const scopeType = (membership.scope_type ?? 'shop') as FlooScopeType;
    const scopeId = (membership.scope_id ?? null) as string | null;
    const orgIdFromMembership = (membership.organization_id ?? null) as string | null;
    const orgIdFromShop = (activeShop?.organization_id ?? null) as string | null;

    if (role === 'OWNER' || role === 'ORG_ADMIN') {
      const orgId = orgIdFromMembership ?? orgIdFromShop;
      return { type: 'organization', id: orgId };
    }
    // For MANAGER and below: respect the literal scope_type.
    if (scopeType === 'organization') {
      const orgId = scopeId ?? orgIdFromMembership ?? orgIdFromShop;
      return { type: 'organization', id: orgId };
    }
    if (scopeType === 'region' || scopeType === 'district') {
      return { type: scopeType, id: scopeId };
    }
    // Shop boundary — the user can only view their assigned shop.
    return { type: 'shop', id: scopeId ?? activeShop?.id ?? null };
  }, [membership, activeShop?.id, activeShop?.organization_id]);

  const boundaryOrgId: string | null = useMemo(() => {
    if (!boundary) return null;
    if (boundary.type === 'organization') return boundary.id;
    // For region/district/shop boundaries, derive the parent org id via
    // the active shop's organization_id (single-org per user in normal
    // operation today).
    return activeShop?.organization_id ?? null;
  }, [boundary, activeShop?.organization_id]);

  // ── Hierarchy data fetches (same query keys as FlooNetworkMap) ──────
  // Cache hit when both surfaces are mounted (TopBar + Map).
  const orgIds = useMemo(() => {
    const ids = new Set<string>();
    if (boundaryOrgId) ids.add(boundaryOrgId);
    for (const s of userShops) {
      if (s.organization_id) ids.add(s.organization_id);
    }
    return Array.from(ids).sort();
  }, [boundaryOrgId, userShops]);
  const orgIdsKey = orgIds.join(',');

  const orgsQuery = useQuery({
    queryKey: ['floo-network-map', 'organizations', orgIdsKey],
    enabled: orgIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id,name,created_at')
        .in('id', orgIds)
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
  const regionsQuery = useQuery({
    queryKey: ['floo-network-map', 'regions', orgIdsKey],
    enabled: orgIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('regions')
        .select('id,name,organization_id,archived_at')
        .in('organization_id', orgIds)
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
  const districtsQuery = useQuery({
    queryKey: ['floo-network-map', 'districts', orgIdsKey],
    enabled: orgIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('districts')
        .select('id,name,organization_id,region_id,archived_at')
        .in('organization_id', orgIds)
        .order('name', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  // ── Option derivation ───────────────────────────────────────────────
  const options = useMemo<FlooScopeOption[]>(() => {
    if (!boundary || !boundaryOrgId) return [];

    const orgs = (orgsQuery.data ?? []) as Array<{ id: string; name: string }>;
    const regions = (regionsQuery.data ?? []) as Array<{
      id: string; name: string; organization_id: string; archived_at: string | null;
    }>;
    const districts = (districtsQuery.data ?? []) as Array<{
      id: string; name: string; organization_id: string;
      region_id: string | null; archived_at: string | null;
    }>;

    // Active-only filters: archived regions/districts and closed shops
    // are hidden from the switcher per Phase 6 spec.
    const activeRegions = regions.filter((r) => r.organization_id === boundaryOrgId && !r.archived_at);
    const activeDistricts = districts.filter((d) => d.organization_id === boundaryOrgId && !d.archived_at);
    // `shop_status` enum is `'active' | 'canceled' | 'suspended'`; only
    // active shops appear as switchable view options. This is stricter
    // than the existing FlooNetworkMap which mis-checked `=== 'closed'`
    // (pre-Phase-6 dead path); the switcher uses the schema-correct
    // value so the option list reflects reality.
    const activeShops = userShops.filter(
      (s) => s.organization_id === boundaryOrgId && s.shop_status === 'active',
    );

    const org = orgs.find((o) => o.id === boundaryOrgId) ?? null;
    const orgLabel = org?.name ?? 'Organization';

    const result: FlooScopeOption[] = [];

    if (boundary.type === 'organization') {
      if (org) {
        result.push({
          type: 'organization',
          id: org.id,
          label: org.name,
          levelLabel: LEVEL_LABEL.organization,
        });
      }
      for (const r of activeRegions) {
        result.push({
          type: 'region',
          id: r.id,
          label: r.name,
          parentLabel: orgLabel,
          levelLabel: LEVEL_LABEL.region,
        });
      }
      for (const d of activeDistricts) {
        const region = d.region_id ? activeRegions.find((r) => r.id === d.region_id) : null;
        result.push({
          type: 'district',
          id: d.id,
          label: d.name,
          parentLabel: region?.name ?? orgLabel,
          levelLabel: LEVEL_LABEL.district,
        });
      }
      for (const s of activeShops) {
        result.push({
          type: 'shop',
          id: s.id,
          label: s.name,
          parentLabel: orgLabel,
          levelLabel: LEVEL_LABEL.shop,
        });
      }
    } else if (boundary.type === 'region' && boundary.id) {
      const region = activeRegions.find((r) => r.id === boundary.id);
      if (region) {
        result.push({
          type: 'region',
          id: region.id,
          label: region.name,
          parentLabel: orgLabel,
          levelLabel: LEVEL_LABEL.region,
        });
        const inRegionDistricts = activeDistricts.filter((d) => d.region_id === region.id);
        for (const d of inRegionDistricts) {
          result.push({
            type: 'district',
            id: d.id,
            label: d.name,
            parentLabel: region.name,
            levelLabel: LEVEL_LABEL.district,
          });
        }
        const inRegionDistrictIds = new Set(inRegionDistricts.map((d) => d.id));
        for (const s of activeShops) {
          if (
            s.region_id === region.id ||
            (s.district_id && inRegionDistrictIds.has(s.district_id))
          ) {
            result.push({
              type: 'shop',
              id: s.id,
              label: s.name,
              parentLabel: region.name,
              levelLabel: LEVEL_LABEL.shop,
            });
          }
        }
      }
    } else if (boundary.type === 'district' && boundary.id) {
      const district = activeDistricts.find((d) => d.id === boundary.id);
      if (district) {
        result.push({
          type: 'district',
          id: district.id,
          label: district.name,
          parentLabel: orgLabel,
          levelLabel: LEVEL_LABEL.district,
        });
        for (const s of activeShops) {
          if (s.district_id === district.id) {
            result.push({
              type: 'shop',
              id: s.id,
              label: s.name,
              parentLabel: district.name,
              levelLabel: LEVEL_LABEL.shop,
            });
          }
        }
      }
    } else if (boundary.type === 'shop' && boundary.id) {
      const shop = activeShops.find((s) => s.id === boundary.id);
      if (shop) {
        result.push({
          type: 'shop',
          id: shop.id,
          label: shop.name,
          parentLabel: orgLabel,
          levelLabel: LEVEL_LABEL.shop,
        });
      }
    }

    return result;
  }, [boundary, boundaryOrgId, orgsQuery.data, regionsQuery.data, districtsQuery.data, userShops]);

  // ── Active scope state (with persistence + validation) ──────────────
  // Initial state is read lazily once per user-id change. After options
  // resolve, we validate the stored choice; if it's no longer in
  // `options`, we fall back to the boundary's natural scope.
  const [active, setActive] = useState<FlooScopeOption | null>(null);

  // Reset when user changes.
  useEffect(() => {
    setActive(null);
  }, [userId]);

  // Once options are known, reconcile active scope.
  useEffect(() => {
    if (options.length === 0) {
      if (active !== null) setActive(null);
      return;
    }

    // If the current active scope is still valid, keep it.
    if (active && options.some((o) => o.type === active.type && o.id === active.id)) {
      // Ensure boundary still allows this scope (defensive — boundary
      // could have changed while options didn't).
      if (boundary && LEVEL_RANK[active.type] <= LEVEL_RANK[boundary.type]) {
        return;
      }
    }

    // Try to restore from storage.
    const stored = readStored(userId);
    if (stored) {
      const match = options.find((o) => o.type === stored.type && o.id === stored.id);
      if (match && boundary && LEVEL_RANK[match.type] <= LEVEL_RANK[boundary.type]) {
        setActive(match);
        return;
      }
      // Stored value is stale — drop it so we don't keep resurrecting it.
      writeStored(userId, null);
    }

    // Fallback to the boundary's natural scope — the first option of
    // the boundary's level, which is typically the boundary itself.
    const fallback =
      options.find((o) => boundary && o.type === boundary.type && o.id === boundary.id) ??
      options[0] ??
      null;
    setActive(fallback);
  }, [options, boundary, userId, active]);

  const setActiveScope = useCallback(
    (option: FlooScopeOption | null) => {
      if (!option) {
        setActive(null);
        writeStored(userId, null);
        return;
      }
      // Enforce boundary at the setter — never permit setting a scope
      // outside the user's reach, regardless of UI source.
      if (!boundary) return;
      if (LEVEL_RANK[option.type] > LEVEL_RANK[boundary.type]) return;
      // Enforce membership-of-the-options-list: the option must come
      // from the derived list. Don't trust arbitrary callers.
      const allowed = options.find((o) => o.type === option.type && o.id === option.id);
      if (!allowed) return;
      setActive(allowed);
      writeStored(userId, { type: allowed.type, id: allowed.id });
    },
    [boundary, options, userId],
  );

  const isLoading =
    orgsQuery.isLoading || regionsQuery.isLoading || districtsQuery.isLoading;
  const error =
    (orgsQuery.error ?? regionsQuery.error ?? districtsQuery.error ?? null) as Error | null;

  // Only show switcher UI when there's more than one option to switch
  // between. A single shop is not a "switch" — it's just a label.
  const canSwitchScope = options.length > 1;

  return {
    options,
    activeScope: active,
    setActiveScope,
    isLoading,
    error,
    canSwitchScope,
    boundary,
  };
}

// Test exports — exposed so doctrinal-scan tests can verify exact
// rule names without hardcoding string searches.
export const __testOnly = {
  LEVEL_RANK,
  LEVEL_LABEL,
};
